import { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useMutation, useLazyQuery } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Autocomplete from "@/components/Autocomplete";
import DateSelect from "@/components/DateSelect";
import TextField from "@/components/TextField";
import Banner from "@/components/Banner";
import Chip, { ChipSet } from "@/components/Chip";
import ListItem from "@/components/ListItem";
import SegmentedButton from "@/components/SegmentedButton";
import Select from "@/components/Select";
import {
  CREATE_UNIVERSITY_PROBATION_MUTATION,
  CREATE_UNIVERSITY_PROBATIONS_MUTATION,
  END_UNIVERSITY_PROBATION_MUTATION,
  UPDATE_UNIVERSITY_PROBATION_MUTATION
} from "./mutations";
import { STUDENTS_QUERY } from "./queries";
import {
  UniversityProbation,
  CreateUniversityProbationInput,
  CreateUniversityProbationsInput,
  EndUniversityProbationInput,
  CreateUniversityProbationResponse,
  CreateUniversityProbationsResponse,
  EndUniversityProbationResponse,
  UpdateUniversityProbationInput,
  UpdateUniversityProbationResponse
} from "./types";
import { PROBATION_TYPE_OPTIONS, type UniversityProbationType } from "./_constants";

type Probation = UniversityProbation;

type StudentOption = {
  id: string;
  name: string;
  pgaId?: string;
  profilePhoto?: string;
};

type StudentNode = {
  id: string;
  pgaId: string;
  displayName: string;
  profilePhoto?: string;
};

type SelectionMode = "individual" | "multiple";

type ProbationFormValues = {
  selectionMode: SelectionMode;
  student: StudentOption | null;
  studentIds: string[];
  startDate: { startDate: string | null; endDate: null };
  endDate: { startDate: string | null; endDate: null };
  probationType?: UniversityProbationType;
  noteTitle: string;
  noteContent: string;
};

type ProbationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** CCMS / URL legacy university id. Create mutations look up by legacy_id, not UUID. */
  universityLegacyId: string;
  mode: "create" | "end" | "edit";
  probation: Probation | null;
  selectedStudent?: {
    id: string;
    displayName: string;
  } | null;
  existingActiveProbationCount?: number;
};

const SELECTION_MODE_OPTIONS = [
  { label: "Individual", value: "individual" },
  { label: "Multiple", value: "multiple" }
];

function toStudentOption(student: StudentNode): StudentOption {
  return {
    id: student.id,
    name: student.displayName,
    pgaId: student.pgaId,
    profilePhoto: student.profilePhoto
  };
}

function matchesStudentSearch(student: StudentOption, query: string) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return false;

  return [student.name, student.pgaId].some((value) => value?.toLowerCase().includes(normalized));
}

export default function ProbationModal({
  isOpen,
  onClose,
  onSuccess,
  universityLegacyId,
  mode,
  probation,
  selectedStudent,
  existingActiveProbationCount = 0
}: ProbationModalProps) {
  const isCreateMode = mode === "create";
  const isEditMode = mode === "edit";
  const canChooseSelectionMode = isCreateMode && !selectedStudent;
  const [error, setError] = useState<string | null>(null);
  const [hasModalOpened, setHasModalOpened] = useState(false);
  const [multipleSearchQuery, setMultipleSearchQuery] = useState("");
  const [selectedStudentsById, setSelectedStudentsById] = useState<Record<string, StudentOption>>({});

  const formMethods = useForm<ProbationFormValues>({
    defaultValues: {
      selectionMode: "individual",
      student: null,
      studentIds: [],
      startDate: { startDate: null, endDate: null },
      endDate: { startDate: null, endDate: null },
      probationType: undefined,
      noteTitle: isCreateMode ? "Probation Started" : "Probation Ended",
      noteContent: ""
    }
  });
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors, isDirty, isValid }
  } = formMethods;

  const selectionMode = useWatch({ control, name: "selectionMode" }) || "individual";
  const selectedStudentIds = useWatch({ control, name: "studentIds" }) || [];
  const startDateValue = useWatch({ control, name: "startDate" });
  const endDateValue = useWatch({ control, name: "endDate" });
  const probationTypeValue = useWatch({ control, name: "probationType" });

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [search, setSearch] = useState("");

  const [getStudents, { data: studentsData, loading: studentsLoading }] = useLazyQuery(STUDENTS_QUERY, {
    fetchPolicy: "network-only"
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(debouncedSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!isOpen || !universityLegacyId || !isCreateMode || selectedStudent) return;

    getStudents({
      variables: {
        input: {
          universityLegacyIds: [universityLegacyId],
          alumni: false,
          search: search || undefined
        },
        first: 100
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, isOpen, universityLegacyId, isCreateMode, selectedStudent, selectionMode]);

  useEffect(() => {
    if (isOpen) {
      const studentOption = selectedStudent ? { id: selectedStudent.id, name: selectedStudent.displayName } : null;

      formMethods.reset({
        selectionMode: "individual",
        student: studentOption,
        studentIds: [],
        startDate: { startDate: null, endDate: null },
        endDate: { startDate: null, endDate: null },
        probationType: isEditMode ? (probation?.probationType ?? undefined) : undefined,
        noteTitle: isCreateMode ? "Probation Started" : "Probation Ended",
        noteContent: ""
      });
      if (!selectedStudent) {
        setDebouncedSearchTerm("");
        setSearch("");
        setMultipleSearchQuery("");
        setSelectedStudentsById({});
      }
      setError(null);
    }
  }, [isCreateMode, isEditMode, selectedStudent, probation?.probationType, formMethods, isOpen]);

  const [createProbation, { loading: createLoading }] = useMutation<
    CreateUniversityProbationResponse,
    { input: CreateUniversityProbationInput }
  >(CREATE_UNIVERSITY_PROBATION_MUTATION);

  const [createProbations, { loading: createBulkLoading }] = useMutation<
    CreateUniversityProbationsResponse,
    { input: CreateUniversityProbationsInput }
  >(CREATE_UNIVERSITY_PROBATIONS_MUTATION);

  const [endProbation, { loading: endLoading }] = useMutation<
    EndUniversityProbationResponse,
    { input: EndUniversityProbationInput }
  >(END_UNIVERSITY_PROBATION_MUTATION);

  const [updateProbation, { loading: updateLoading }] = useMutation<
    UpdateUniversityProbationResponse,
    { input: UpdateUniversityProbationInput }
  >(UPDATE_UNIVERSITY_PROBATION_MUTATION);

  const mutationLoading = createLoading || createBulkLoading || endLoading || updateLoading;

  useEffect(() => {
    if (isOpen && !hasModalOpened) {
      const timer = setTimeout(() => {
        setHasModalOpened(true);
      }, 100);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setHasModalOpened(false);
    }
  }, [isOpen, hasModalOpened]);

  const studentOptions: StudentOption[] = useMemo(
    () => (studentsData?.universityStudents?.nodes || []).map((student: StudentNode) => toStudentOption(student)),
    [studentsData]
  );

  const multipleMatchOptions = useMemo(() => {
    return studentOptions.filter(
      (student) => !(student.id in selectedStudentsById) && matchesStudentSearch(student, multipleSearchQuery)
    );
  }, [multipleSearchQuery, selectedStudentsById, studentOptions]);

  const handleSearch = (query: string) => {
    setDebouncedSearchTerm(query);
  };

  const clearMultipleSearch = () => {
    setMultipleSearchQuery("");
    setDebouncedSearchTerm("");
    setSearch("");
  };

  const handleSelectionModeChange = (value: string | null) => {
    const nextMode = (value as SelectionMode) || "individual";
    setValue("selectionMode", nextMode, { shouldDirty: true });
    setValue("student", null, { shouldDirty: true });
    setValue("studentIds", [], { shouldDirty: true });
    setDebouncedSearchTerm("");
    setSearch("");
    setMultipleSearchQuery("");
    setSelectedStudentsById({});
    setError(null);
  };

  const onSubmit = async (formData: ProbationFormValues) => {
    setError(null);
    try {
      if (isEditMode) {
        if (!probation || !formData.probationType) return;

        const input: UpdateUniversityProbationInput = {
          probationId: probation.id,
          probationType: formData.probationType
        };

        const result = await updateProbation({
          variables: { input }
        });

        if (result.data?.updateUniversityProbation?.success) {
          showSnackbar("Probation type has been updated.");
          onSuccess();
          onClose();
        } else {
          setError(result.data?.updateUniversityProbation?.message || "Failed to update probation type.");
        }
        return;
      }

      if (isCreateMode) {
        if (!formData.startDate.startDate) return;

        if (canChooseSelectionMode && formData.selectionMode === "multiple") {
          if (formData.studentIds.length === 0) return;
          if (!formData.probationType) return;

          const input: CreateUniversityProbationsInput = {
            personIds: formData.studentIds,
            universityId: universityLegacyId,
            startedAt: formData.startDate.startDate,
            noteTitle: formData.noteTitle,
            noteContent: formData.noteContent,
            probationType: formData.probationType
          };

          const result = await createProbations({
            variables: { input }
          });

          if (result.data?.createUniversityProbations?.success) {
            showSnackbar(result.data.createUniversityProbations.message || "Students have been placed on probation.");
            onSuccess();
            onClose();
          } else {
            setError(result.data?.createUniversityProbations?.message || "Failed to place students on probation.");
          }
          return;
        }

        if (!formData.student) return;
        if (!formData.probationType) return;

        const input: CreateUniversityProbationInput = {
          personId: formData.student.id,
          universityId: universityLegacyId,
          startedAt: formData.startDate.startDate,
          noteTitle: formData.noteTitle,
          noteContent: formData.noteContent,
          probationType: formData.probationType
        };

        const result = await createProbation({
          variables: { input }
        });

        if (result.data?.createUniversityProbation?.success) {
          showSnackbar("Student has been placed on probation.");
          onSuccess();
          onClose();
        } else {
          setError(result.data?.createUniversityProbation?.message || "Failed to place student on probation.");
        }
      } else {
        if (!probation || !formData.endDate.startDate) return;

        const input: EndUniversityProbationInput = {
          probationId: probation.id,
          endedAt: formData.endDate.startDate,
          noteTitle: formData.noteTitle,
          ...(formData.noteContent ? { noteContent: formData.noteContent } : {})
        };

        const result = await endProbation({
          variables: { input }
        });

        if (result.data?.endUniversityProbation?.success) {
          showSnackbar("Probation has been ended.");
          onSuccess();
          onClose();
        } else {
          setError(result.data?.endUniversityProbation?.message || "Failed to end probation.");
        }
      }
    } catch (submitError: any) {
      if (submitError?.graphQLErrors?.length > 0) {
        const graphQLError = submitError.graphQLErrors[0];
        if (graphQLError?.extensions?.code === "422") {
          setError(graphQLError.message || "Validation failed");
        } else {
          setError(graphQLError.message || "An error occurred");
        }
      } else if (submitError?.networkError) {
        setError("Network error occurred. Please try again.");
      } else {
        setError(
          isCreateMode
            ? "Failed to mark student for probation."
            : isEditMode
              ? "Failed to update probation type."
              : "Failed to end probation."
        );
      }
      console.error(submitError);
    }
  };

  if (!isOpen) return null;

  const isMultipleMode = canChooseSelectionMode && selectionMode === "multiple";
  const selectedStudents = selectedStudentIds
    .map((id) => selectedStudentsById[id])
    .filter((student): student is StudentOption => Boolean(student));
  const title = isEditMode
    ? "Edit Probation Type"
    : isCreateMode
      ? isMultipleMode
        ? "Mark Students for Probation"
        : "Mark Student for Probation"
      : "End Student Probation";
  const submitText = isEditMode ? "Save Probation Type" : isCreateMode ? "Mark for Probation" : "End Probation";

  const hasRequiredStudents = isMultipleMode
    ? selectedStudentIds.length > 0
    : Boolean(selectedStudent || formMethods.watch("student")?.id);

  return (
    <Dialog
      open={isOpen}
      data-testid="probation-modal"
      onClosed={onClose}
      hasFormElements
      displayOverflow
      headline={title}
      className="w-[36rem] max-w-[90vw] max-h-[85vh]"
      actions={
        <div className="flex gap-2">
          <Button variant="filledTonal" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit(onSubmit)}
            disabled={
              (isEditMode
                ? !probationTypeValue || probationTypeValue === probation?.probationType
                : !isDirty || !isValid) ||
              mutationLoading ||
              (isCreateMode && (!startDateValue?.startDate || !hasRequiredStudents)) ||
              (isCreateMode && !probationTypeValue) ||
              (!isCreateMode && !isEditMode && !endDateValue?.startDate)
            }
          >
            {submitText}
          </Button>
        </div>
      }
    >
      <FormProvider {...formMethods}>
        <div className="flex flex-col gap-6">
          {error && (
            <Banner variant="error" description={error} inline={true} closable={false} hideSecondaryDismiss={true} />
          )}
          {isCreateMode && existingActiveProbationCount > 0 && (
            <Banner
              variant="warn"
              inline
              closable={false}
              hideSecondaryDismiss
              description={`This student already has ${existingActiveProbationCount} active ${existingActiveProbationCount === 1 ? "probation" : "probations"}. Placing another will create an additional active probation record.`}
            />
          )}
          {isMultipleMode && selectedStudents.length > 0 && (
            <div
              className="rounded-lg bg-sys-brand-secondary-container px-3 py-2 text-body-medium text-sys-brand-on-secondary-container"
              data-testid="selected-students-summary"
            >
              <div className="font-semibold">Placing on probation ({selectedStudents.length}):</div>
              <div className="mt-2">
                <ChipSet>
                  {selectedStudents.map((student) => (
                    <Chip
                      key={student.id}
                      label={student.name}
                      removable
                      onRemove={(e) => {
                        e?.preventDefault?.();
                        const currentIds = getValues("studentIds") ?? [];
                        setValue(
                          "studentIds",
                          currentIds.filter((id) => id !== student.id),
                          { shouldDirty: true, shouldValidate: true }
                        );
                        setSelectedStudentsById((prev) => {
                          const next = { ...prev };
                          delete next[student.id];
                          return next;
                        });
                      }}
                    />
                  ))}
                </ChipSet>
              </div>
            </div>
          )}
          {isEditMode ? (
            <p>
              Update the probation type for <strong>{probation?.person.displayName}</strong>.
            </p>
          ) : isCreateMode ? (
            selectedStudent ? (
              <div className="text-body-medium">
                <strong>Student:</strong> {selectedStudent.displayName}
              </div>
            ) : (
              <>
                {canChooseSelectionMode && (
                  <Controller
                    name="selectionMode"
                    control={control}
                    render={({ field }) => (
                      <div className="flex flex-col gap-2">
                        <span className="text-body-medium text-sys-brand-on-surface-variant">Selection</span>
                        <SegmentedButton
                          options={SELECTION_MODE_OPTIONS}
                          value={field.value}
                          onChange={handleSelectionModeChange}
                          maxButtonWidth={200}
                          showCheckmark
                          fullWidth
                        />
                      </div>
                    )}
                  />
                )}
                {isMultipleMode ? (
                  <Controller
                    name="studentIds"
                    control={control}
                    rules={{
                      validate: (value) => (value && value.length > 0) || "Select at least one student"
                    }}
                    render={({ field }) => (
                      <div className="flex flex-col gap-2" data-testid="multiple-students-picker">
                        <Autocomplete
                          label="Add Students *"
                          data={multipleMatchOptions}
                          renderItem={(option: StudentOption) => (
                            <ListItem
                              useAvatar
                              compact
                              picture={option.profilePhoto || undefined}
                              iconName="person"
                              headline={option.name}
                              supportingText={option.pgaId ? `PGA ID: ${option.pgaId}` : undefined}
                            />
                          )}
                          query={multipleSearchQuery}
                          onChangeText={(text: string) => {
                            setMultipleSearchQuery(text);
                            handleSearch(text);
                          }}
                          onSelect={(option: StudentOption) => {
                            const currentIds = getValues("studentIds") ?? [];
                            if (!currentIds.includes(option.id)) {
                              field.onChange([...currentIds, option.id]);
                              setSelectedStudentsById((prev) => ({ ...prev, [option.id]: option }));
                            }
                            clearMultipleSearch();
                          }}
                          reset={clearMultipleSearch}
                          loadingResults={studentsLoading}
                          error={!!errors.studentIds}
                          errorText={errors.studentIds?.message}
                          placeholder="Search by name or PGA ID"
                          supportingText="Search to find students, then select each one to add"
                          menuClassName="max-h-[220px]"
                        />
                      </div>
                    )}
                  />
                ) : (
                  <Controller
                    name="student"
                    control={control}
                    rules={{ required: "Student is required" }}
                    render={({ field }) => (
                      <Autocomplete
                        label="Student *"
                        data={studentOptions || []}
                        renderItem={(option: StudentOption) => (
                          <ListItem
                            useAvatar
                            compact
                            picture={option.profilePhoto || undefined}
                            iconName="person"
                            headline={option.name}
                            supportingText={option.pgaId ? `PGA ID: ${option.pgaId}` : undefined}
                          />
                        )}
                        query={field.value?.name || ""}
                        onChangeText={(text: string) => {
                          handleSearch(text);
                          field.onChange({ id: "", name: text });
                        }}
                        onSelect={(option: StudentOption) => {
                          field.onChange(option);
                        }}
                        loadingResults={studentsLoading}
                        error={!!errors.student}
                        errorText={errors.student?.message}
                        placeholder="Search for a student"
                      />
                    )}
                  />
                )}
              </>
            )
          ) : (
            <p>
              Enter the effective date to end the probation for <strong>{probation?.person.displayName}</strong>.
            </p>
          )}

          {isCreateMode ? (
            <Controller
              name="startDate"
              control={control}
              rules={{ required: "Start date is required" }}
              render={({ field, fieldState }) => (
                <DateSelect
                  label="Effective Start Date *"
                  placeholder="Select Date"
                  useModal={true}
                  value={field.value}
                  onChange={field.onChange}
                  useSingleDate
                  showFooter={false}
                  error={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                  disabled={!hasModalOpened}
                />
              )}
            />
          ) : !isEditMode ? (
            <Controller
              name="endDate"
              control={control}
              rules={{ required: "End date is required" }}
              render={({ field, fieldState }) => (
                <DateSelect
                  label="Effective End Date *"
                  placeholder="Select Date"
                  useModal={true}
                  value={field.value}
                  onChange={field.onChange}
                  useSingleDate
                  showFooter={false}
                  error={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                  disabled={!hasModalOpened}
                />
              )}
            />
          ) : null}

          {isCreateMode || isEditMode ? (
            <Controller
              name="probationType"
              control={control}
              rules={{ required: "Probation type is required" }}
              render={({ field, fieldState }) => (
                <Select
                  label="Probation Type *"
                  required
                  options={PROBATION_TYPE_OPTIONS}
                  value={field.value}
                  onSelect={(value) => field.onChange(value as UniversityProbationType)}
                  error={fieldState.invalid}
                  errorText={fieldState.error?.message}
                  placeholder="Select probation type"
                />
              )}
            />
          ) : null}

          {!isEditMode ? (
            <Controller
              name="noteContent"
              control={control}
              rules={{
                ...(isCreateMode ? { required: "Probation reason is required" } : {})
              }}
              render={({ field, fieldState }) => (
                <TextField
                  label={isCreateMode ? "Probation Reason *" : "Note"}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.invalid}
                  errorText={fieldState.error?.message}
                  type="textarea"
                  rows={3}
                  className="w-full"
                  placeholder={
                    isCreateMode
                      ? isMultipleMode
                        ? "Enter the shared reason for placing these students on probation"
                        : "Enter the reason for placing the student on probation (e.g. Academic Performance Issue)"
                      : "Add an optional note about ending this probation"
                  }
                />
              )}
            />
          ) : null}
        </div>
      </FormProvider>
    </Dialog>
  );
}
